<script setup lang="ts">
/**
 * Six buttons that line elements up, and two that spread them out.
 *
 * Used for one element, which lines up with the page, and for several, which
 * line up with one another; what they are lined up with is the panel's
 * business, and these only say which edge. Drawn as the edge and a block
 * against it, the way every layout tool draws them, since that picture is
 * quicker to read than any word for it.
 */
import { useI18n } from 'vue-i18n'

import { ALIGNMENTS, type Alignment } from '@/domain/free'

const { t } = useI18n()

withDefaults(
  defineProps<{
    /** Offer spreading as well, which only means anything for several. */
    spreading?: boolean
    /** Spreading needs three, since two have no gap between them to even out. */
    canSpread?: boolean
  }>(),
  { spreading: false, canSpread: false },
)

const emit = defineEmits<{
  align: [how: Alignment]
  spread: [axis: 'x' | 'y']
}>()
</script>

<template>
  <div class="align">
    <div class="segments" role="group" :aria-label="t('inspector.layer.alignOnPage')">
      <button
        v-for="how in ALIGNMENTS"
        :key="how"
        type="button"
        class="segment"
        :title="t(`inspector.layer.alignments.${how}`)"
        :aria-label="t(`inspector.layer.alignments.${how}`)"
        @click="emit('align', how)"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
          <template v-if="how === 'left'">
            <path d="M2.5 2v12" stroke="currentColor" stroke-width="1.4" />
            <rect x="4.5" y="5" width="8" height="6" rx="1" fill="currentColor" />
          </template>
          <template v-else-if="how === 'centre'">
            <path d="M8 2v12" stroke="currentColor" stroke-width="1.4" />
            <rect x="4" y="5" width="8" height="6" rx="1" fill="currentColor" />
          </template>
          <template v-else-if="how === 'right'">
            <path d="M13.5 2v12" stroke="currentColor" stroke-width="1.4" />
            <rect x="3.5" y="5" width="8" height="6" rx="1" fill="currentColor" />
          </template>
          <template v-else-if="how === 'top'">
            <path d="M2 2.5h12" stroke="currentColor" stroke-width="1.4" />
            <rect x="5" y="4.5" width="6" height="8" rx="1" fill="currentColor" />
          </template>
          <template v-else-if="how === 'middle'">
            <path d="M2 8h12" stroke="currentColor" stroke-width="1.4" />
            <rect x="5" y="4" width="6" height="8" rx="1" fill="currentColor" />
          </template>
          <template v-else>
            <path d="M2 13.5h12" stroke="currentColor" stroke-width="1.4" />
            <rect x="5" y="3.5" width="6" height="8" rx="1" fill="currentColor" />
          </template>
        </svg>
      </button>
    </div>

    <div v-if="spreading" class="segments" role="group" :aria-label="t('inspector.layer.spread')">
      <button
        v-for="axis in ['x', 'y'] as const"
        :key="axis"
        type="button"
        class="segment segment--wide"
        :disabled="!canSpread"
        :title="t(`inspector.layer.spreads.${axis}`)"
        @click="emit('spread', axis)"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
          <template v-if="axis === 'x'">
            <rect x="1.5" y="5" width="3" height="6" rx="0.8" fill="currentColor" />
            <rect x="6.5" y="5" width="3" height="6" rx="0.8" fill="currentColor" />
            <rect x="11.5" y="5" width="3" height="6" rx="0.8" fill="currentColor" />
          </template>
          <template v-else>
            <rect x="5" y="1.5" width="6" height="3" rx="0.8" fill="currentColor" />
            <rect x="5" y="6.5" width="6" height="3" rx="0.8" fill="currentColor" />
            <rect x="5" y="11.5" width="6" height="3" rx="0.8" fill="currentColor" />
          </template>
        </svg>
        {{ t(`inspector.layer.spreads.${axis}`) }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.align {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
}

.segments {
  display: flex;
  flex-wrap: wrap;
  gap: 1px;
  padding: 2px;
  border-radius: var(--radius);
  background: var(--panel-raised);
  box-shadow: inset 0 0 0 1px var(--hairline-strong);
}

.segment {
  flex: 1;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  height: 26px;
  padding: 0 var(--sp-3);
  border: 0;
  border-radius: var(--radius-xs);
  background: none;
  color: var(--ink-soft);
  font: inherit;
  font-size: var(--text-xs);
  white-space: nowrap;
  cursor: pointer;
  transition:
    background var(--quick),
    color var(--quick);
}

.segment:hover:not(:disabled) {
  background: var(--panel-hover);
  color: var(--ink);
}

.segment:disabled {
  opacity: 0.4;
  cursor: default;
}
</style>
